import React from 'react'
import { TouchableOpacity } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import { useSelector } from 'react-redux'
import { StyleService } from '@src/style/service'
import { InlineAlert } from '@src/components/InlineAlert'
import { User } from '@src/utils'
import { uxSensorModeKindSelector } from '@src/selectors/app'
import {
  useDexcomConnection,
  useDexcomConnectionKind,
  useDexcomConnectionSensorName,
  useIsActiveDexcom,
} from '@src/utils/hooks'
import {
  DexcomConnectionKind,
  DexcomConnectionStatus,
  MobileAppFeature,
  UiInlineAlert,
  UserExperienceSensorModeKind,
} from '@src/types'
import { useShowSteloSetupPopupOrchestrator } from './hooks/useShowSteloSetupPopupOrchestrator'

export const MissingDexcomIntegrationPopup = () => {
  const navigation = useNavigation()
  const dexcomConnection = useDexcomConnection()
  const isActiveDexcom = useIsActiveDexcom()
  const uxSensorModeKind = useSelector(uxSensorModeKindSelector)
  const dexcomConnectionKind = useDexcomConnectionKind()
  const hasSteloApiAccess = User.hasFeature(MobileAppFeature.DexcomSteloApiIntegration)
  const hasDexcomG7ApiAccess = User.hasFeature(MobileAppFeature.DexcomG7ApiIntegration)
  const hasDexcomG6ApiAccess = User.hasFeature(MobileAppFeature.DexcomG6ApiIntegration)
  const hideSteloConnectPrompt =
    dexcomConnectionKind === DexcomConnectionKind.Stelo && !hasSteloApiAccess
  const isDexcomG7SensorMode =
    uxSensorModeKind === UserExperienceSensorModeKind.ActivePrescriptionDexcomG7 ||
    uxSensorModeKind === UserExperienceSensorModeKind.OwnSensorDexcomG7
  const isDexcomG6SensorMode = uxSensorModeKind === UserExperienceSensorModeKind.OwnSensorDexcomG6
  const hideDexcomG7ConnectPrompt = isDexcomG7SensorMode && !hasDexcomG7ApiAccess
  const hideDexcomG6ConnectPrompt = isDexcomG6SensorMode && !hasDexcomG6ApiAccess

  const { shouldShow: alreadyShowingSteloPopup } = useShowSteloSetupPopupOrchestrator()

  const sensorName = useDexcomConnectionSensorName()

  const hidePopup =
    !isActiveDexcom ||
    dexcomConnection?.status === DexcomConnectionStatus.Active ||
    alreadyShowingSteloPopup ||
    hideSteloConnectPrompt ||
    hideDexcomG7ConnectPrompt ||
    hideDexcomG6ConnectPrompt

  if (hidePopup) {
    return null
  }

  const navigateToSensorSettings = () => {
    return navigation.navigate('DexcomSensorSettings')
  }

  return (
    <TouchableOpacity
      accessibilityLabel="Enable dexcom integration"
      activeOpacity={0.7}
      onPress={navigateToSensorSettings}
    >
      {dexcomConnection?.status === DexcomConnectionStatus.Invalidated ? (
        <InlineAlert
          style={styles.popup}
          category={UiInlineAlert.Danger}
          message={`Your ${sensorName} data integration authentication has been reset. Please authenticate again to integrate your data.`}
        />
      ) : (
        <InlineAlert
          style={styles.popup}
          category={UiInlineAlert.Warning}
          message={`Connect your ${sensorName} data to continue`}
        />
      )}
    </TouchableOpacity>
  )
}

const styles = StyleService.create({
  popup: {
    marginHorizontal: 16,
    marginTop: 8,
  },
})
